/**
 * Google Analytics 4 (gtag.js) helper.
 *
 * NOTE: The configured id `G-VZB5RQPL3X` is a GA4 *Measurement ID*, not a GTM
 * container id (`GTM-XXXXXXX`). GA4 ids load via gtag.js, so this file talks to
 * gtag directly. The base snippet lives in `public/index.html`; these helpers
 * send SPA page views and custom events.
 *
 * Events sent from here:
 *   - `page_view`    -> on every SPA route change (see usePageTracking)
 *   - `button_click` -> on every button/link click (see initButtonTracking)
 *   - anything you send manually via `trackEvent`
 *
 * PHI SAFETY: this is a patient system. `sanitizePath` masks numeric / long id
 * segments so patient & record ids never reach analytics. Never send patient
 * names, DOB, MRN, etc. in event params.
 */

export const GA_ID = process.env.REACT_APP_GTM_ID;

// Prod is identified by the prod API server. Because the app toggles
// REACT_APP_SERVER_URL per environment, this keeps analytics OFF on
// local + staging automatically.
const PROD_SERVER = 'coresys.mapyourmeds.com';
export const isProd = () =>
  (process.env.REACT_APP_SERVER_URL || '').includes(PROD_SERVER);

// Analytics only runs when a GA id is set AND we're pointed at prod.
export const isAnalyticsEnabled = () => Boolean(GA_ID) && isProd();

// gtag() pushes its arguments onto the dataLayer; GA4 consumes them. Defined
// here so route/click tracking never depends on the inline snippet load order.
export const gtag = (...args) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

/**
 * Load gtag.js and configure GA4 — ONLY in prod. On local/staging this is a
 * no-op, so no analytics script is ever fetched or any hit sent. Idempotent.
 */
export const initAnalytics = () => {
  if (!isAnalyticsEnabled() || window.__ga4Loaded) return;
  window.__ga4Loaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA_ID;
  document.head.appendChild(s);

  gtag('js', new Date());
  // send_page_view:false -> SPA page views are sent manually from React.
  gtag('config', GA_ID, { send_page_view: false });
};

/**
 * Mask id-like URL segments so no PHI leaks into analytics.
 * e.g. `/main/patient/details/64f1ab...` -> `/main/patient/details/:id`
 */
export const sanitizePath = (path = '') =>
  path
    .split('/')
    .map(seg =>
      /^\d+$/.test(seg) || /^[0-9a-f]{16,}$/i.test(seg) ? ':id' : seg
    )
    .join('/');

/**
 * Readable name for the current page, derived from the (sanitized) route.
 * e.g. `/main/patient/edit-patient/123` -> "Edit Patient"
 * Used to tag button clicks (esp. form Save/Update) with their screen.
 */
export const getPageName = (path = window.location.pathname) => {
  const parts = sanitizePath(path)
    .split('/')
    .filter(seg => seg && seg !== 'main' && seg !== ':id');
  const last = parts[parts.length - 1] || 'home';
  return last.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Send an SPA page-view. gtag `config` uses `send_page_view:false`, so route
 * changes must be reported here (otherwise only the first load is counted).
 */
export const trackPageView = (path, title) => {
  if (!isAnalyticsEnabled()) return;
  const cleanPath = sanitizePath(path);
  gtag('event', 'page_view', {
    page_path: cleanPath,
    page_title: title || document.title,
    page_location: window.location.origin + cleanPath
  });
};

/**
 * Send a custom GA4 event.
 * @param {string} event  event name.
 * @param {object} params event parameters (must NOT contain PHI).
 */
export const trackEvent = (event, params = {}) => {
  if (!event || !isAnalyticsEnabled()) return;
  gtag('event', event, params);
};

/**
 * Attach one delegated click listener that reports every button/anchor click
 * as a `button_click` event. Idempotent. Override the label with
 * `data-gtm-label="..."`, or opt an element in with `data-gtm-click`.
 */
export const initButtonTracking = () => {
  if (typeof document === 'undefined' || window.__ga4ClickBound) return;
  window.__ga4ClickBound = true;

  document.addEventListener(
    'click',
    event => {
      const el =
        event.target &&
        event.target.closest &&
        event.target.closest('button, a, [role="button"], [data-gtm-click]');
      if (!el) return;

      const label =
        el.getAttribute('data-gtm-label') ||
        el.getAttribute('aria-label') ||
        (el.textContent && el.textContent.trim().slice(0, 100)) ||
        el.getAttribute('title') ||
        el.id ||
        '(no label)';

      trackEvent('button_click', {
        click_text: label,
        click_id: el.id || undefined,
        click_tag: el.tagName ? el.tagName.toLowerCase() : undefined,
        click_url:
          el.getAttribute && el.getAttribute('href')
            ? el.getAttribute('href')
            : undefined,
        // page context so form Save/Update clicks are attributed to a screen
        page_name: getPageName(),
        page_path: sanitizePath(window.location.pathname)
      });
    },
    true // capture phase, so clicks are caught even when handlers stopPropagation
  );
};
